import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Inbox, Info, Loader2, Pencil, Plus, Trash2 } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { cn } from '@/shared/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { usePermissions } from '@/shared/hooks/usePermissions'
import { InstallationDeleteConfirmDialog } from '@/features/installations/components/installation-delete-confirm-dialog'
import { SourceGroupReferenceBlockedDialog } from '@/features/installations/components/source-group-reference-blocked-dialog'
import {
  useCbamGoodsCodeLookupQuery,
  useInstallationPrecursorCnCodeMutations,
  useInstallationPrecursorCnCodesQuery,
} from '@/features/installations/hooks/useInstallationConfiguration'
import { useInstallationDeleteConfirm } from '@/features/installations/hooks/use-installation-delete-confirm'
import { isSourceGroupReferenceConflict, blockedSourceGroupCount } from '@/features/installations/lib/is-source-group-reference-conflict'
import { precursorCnCodeSourceGroupsPath } from '@/features/installations/lib/precursor-cn-code-source-groups-path'
import { handleServerError } from '@/shared/lib/handle-server-error'
import {
  precursorSupplyOriginValues,
  type InstallationPrecursorSupplyOrigin,
} from '@/features/installations/api/installation-configuration-model'

const CN_DIGITS_PATTERN = /^\d{8}$/

const defaultSupplyOrigin: InstallationPrecursorSupplyOrigin = 'EXTERNAL'

function PrecursorSupplyOriginFields({
  value,
  onChange,
  namePrefix,
  t,
}: {
  value: InstallationPrecursorSupplyOrigin
  onChange: (next: InstallationPrecursorSupplyOrigin) => void
  namePrefix: string
  t: TFunction<'installations'>
}) {
  return (
    <fieldset className="m-0 min-w-0 space-y-3 border-0 p-0">
      <legend className="mb-2 w-full text-sm font-medium leading-none">
        {t('configuration.precursorCn.supplyOriginField')}
      </legend>
      <div className="flex flex-col gap-3">
        {precursorSupplyOriginValues.map((origin) => {
          const inputId = `${namePrefix}-${origin}`
          return (
            <div key={origin} className="flex items-start gap-3">
              <input
                id={inputId}
                type="radio"
                name={namePrefix}
                value={origin}
                checked={value === origin}
                onChange={() => {
                  onChange(origin)
                }}
                className="peer mt-1 size-4 shrink-0 cursor-pointer accent-primary"
              />
              <Label
                htmlFor={inputId}
                className="max-w-none cursor-pointer font-normal leading-snug peer-disabled:cursor-not-allowed peer-disabled:opacity-50"
              >
                {t(`configuration.precursorCn.supplyOriginLabels.${origin}`)}
              </Label>
            </div>
          )
        })}
      </div>
    </fieldset>
  )
}

export function InstallationPrecursorCnCodesPage() {
  const { t } = useTranslation('installations')
  const { checkPermission } = usePermissions()
  const canEdit = checkPermission('installation', 'update')
  const navigate = useNavigate()
  const { installationId } = useParams<{ installationId: string }>()

  const listQuery = useInstallationPrecursorCnCodesQuery()
  const mutations = useInstallationPrecursorCnCodeMutations()
  const deleteConfirm = useInstallationDeleteConfirm()
  const [sourceGroupBlocked, setSourceGroupBlocked] = useState<{
    id: string
    label: string
    count: number
  } | null>(null)

  const [createOpen, setCreateOpen] = useState(false)
  const [precursorInput, setPrecursorInput] = useState('')
  const [createSupplyOrigin, setCreateSupplyOrigin] =
    useState<InstallationPrecursorSupplyOrigin>(defaultSupplyOrigin)

  const rows = listQuery.data

  const trimmedPrecursorInput = precursorInput.trim()
  const hasValidPrecursorInput = CN_DIGITS_PATTERN.test(trimmedPrecursorInput)
  const isPrecursorAlreadyAdded =
    hasValidPrecursorInput &&
    Boolean(rows?.some((row) => row.cnCode === trimmedPrecursorInput))

  const cbamRelevanceQuery = useCbamGoodsCodeLookupQuery({
    goodsCode8Digits: precursorInput,
    enabled: createOpen && hasValidPrecursorInput && !isPrecursorAlreadyAdded,
  })

  const showCnCodeFeedback = createOpen && hasValidPrecursorInput
  const isPrecursorInGoodsList =
    !isPrecursorAlreadyAdded &&
    cbamRelevanceQuery.isSuccess &&
    cbamRelevanceQuery.data.cbamRelevant
  const isPrecursorNotInGoodsList =
    !isPrecursorAlreadyAdded &&
    cbamRelevanceQuery.isSuccess &&
    !cbamRelevanceQuery.data.cbamRelevant

  const resetCreateForm = () => {
    setPrecursorInput('')
    setCreateSupplyOrigin(defaultSupplyOrigin)
  }

  const submitPrecursor = async () => {
    const value = precursorInput.trim()
    if (!CN_DIGITS_PATTERN.test(value)) {
      return
    }
    if (rows?.some((row) => row.cnCode === value)) {
      return
    }
    await mutations.addPrecursorCnCode.mutateAsync({
      cnCode: value,
      supplyOrigin: createSupplyOrigin,
    })
    resetCreateForm()
    setCreateOpen(false)
  }

  const goToOtherMaterialCategories = () => {
    if (!installationId) {
      return
    }
    setCreateOpen(false)
    resetCreateForm()
    navigate(
      `/dashboard/${installationId}/installation/configuration/other-material-categories`
    )
  }

  const handleConfirmDelete = async () => {
    if (!deleteConfirm.pendingDelete) {
      return
    }
    try {
      await mutations.removePrecursorCnCode.mutateAsync(deleteConfirm.pendingDelete.id)
      deleteConfirm.clearPendingDelete()
    } catch (error) {
      if (isSourceGroupReferenceConflict(error)) {
        const pending = deleteConfirm.pendingDelete
        const row = rows?.find((item) => item.id === pending.id)
        deleteConfirm.clearPendingDelete()
        setSourceGroupBlocked({
          id: pending.id,
          label: pending.label,
          count: blockedSourceGroupCount(row?.sourceGroupCount),
        })
        return
      }
      handleServerError(error)
    }
  }

  const requestPrecursorDelete = (row: {
    id: string
    cnCode: string
    sourceGroupCount: number
  }) => {
    if (row.sourceGroupCount > 0) {
      setSourceGroupBlocked({
        id: row.id,
        label: row.cnCode,
        count: row.sourceGroupCount,
      })
      return
    }
    deleteConfirm.requestDelete({
      id: row.id,
      label: row.cnCode,
    })
  }

  if (listQuery.isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (rows === undefined) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-6">
        <p className="text-muted-foreground">{t('configuration.loadError')}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('configuration.precursorCn.title')}
          </h1>
          <p className="text-muted-foreground">{t('configuration.precursorCn.description')}</p>
        </div>
        {canEdit && rows.length > 0 && (
          <Button
            type="button"
            onClick={() => {
              resetCreateForm()
              setCreateOpen(true)
            }}
          >
            <Plus className="size-4" />
            {t('common:actions.create')}
          </Button>
        )}
      </div>

      <Alert>
        <Info className="size-4 shrink-0" aria-hidden />
        <AlertDescription>{t('configuration.precursorCn.scopeNotice')}</AlertDescription>
      </Alert>

      {rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-4 rounded-md border border-dashed py-16">
          <Inbox className="size-10 text-muted-foreground/50" />
          <div className="space-y-1 text-center">
            <h3 className="text-lg font-semibold">{t('configuration.precursorCn.emptyTitle')}</h3>
            <p className="max-w-md text-sm text-muted-foreground">
              {t('configuration.precursorCn.emptyDescription')}
            </p>
          </div>
          {canEdit && (
            <Button
              type="button"
              onClick={() => {
                resetCreateForm()
                setCreateOpen(true)
              }}
            >
              <Plus className="size-4" />
              {t('common:actions.create')}
            </Button>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow className="group/row">
                <TableHead
                  className={cn(
                    'bg-background group-hover/row:bg-muted',
                    'min-w-0 max-w-[38%] whitespace-normal'
                  )}
                >
                  {t('configuration.table.cnCode')}
                </TableHead>
                <TableHead
                  className={cn(
                    'bg-background group-hover/row:bg-muted',
                    'min-w-0 max-w-[42%] whitespace-normal'
                  )}
                >
                  {t('configuration.table.sourceGroupCount')}
                </TableHead>
                {canEdit && (
                  <TableHead
                    className={cn(
                      'bg-background group-hover/row:bg-muted',
                      'w-[120px] min-w-0 max-w-[120px] text-end'
                    )}
                  >
                    {t('configuration.table.actions')}
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} className="group/row">
                  <TableCell
                    className={cn(
                      'bg-background group-hover/row:bg-muted',
                      'min-w-0 max-w-[38%] whitespace-normal font-mono'
                    )}
                  >
                    {row.cnCode}
                  </TableCell>
                  <TableCell
                    className={cn(
                      'bg-background group-hover/row:bg-muted',
                      'min-w-0 max-w-[42%] whitespace-normal'
                    )}
                  >
                    {row.sourceGroupCount}
                  </TableCell>
                  {canEdit && (
                    <TableCell
                      className={cn(
                        'bg-background group-hover/row:bg-muted',
                        'w-[120px] min-w-0 max-w-[120px] text-end'
                      )}
                    >
                      <div className="flex justify-end gap-1">
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          asChild
                        >
                          <Link
                            to={`/dashboard/${installationId}/installation/configuration/precursor-cn-codes/${row.id}/source-groups`}
                            aria-label={t('configuration.a11y.editPrecursorSourceGroups', {
                              cnCode: row.cnCode,
                            })}
                          >
                            <Pencil className="size-4" />
                          </Link>
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="text-destructive"
                          aria-label={t('configuration.a11y.deletePrecursorCn', {
                            cnCode: row.cnCode,
                          })}
                          onClick={() => requestPrecursorDelete(row)}
                          disabled={mutations.removePrecursorCnCode.isPending}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open)
          if (!open) {
            resetCreateForm()
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('configuration.dialog.addPrecursorCn')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="precursor-cn-create-input">{t('configuration.table.cnCode')}</Label>
              <Input
                id="precursor-cn-create-input"
                inputMode="numeric"
                maxLength={8}
                placeholder={t('configuration.cnPlaceholder')}
                value={precursorInput}
                onChange={(event) => setPrecursorInput(event.target.value.replace(/\D/g, ''))}
              />
            </div>
            {showCnCodeFeedback && isPrecursorAlreadyAdded && (
              <Alert>
                <AlertDescription>{t('configuration.cnCodeAlreadyAdded')}</AlertDescription>
              </Alert>
            )}
            {showCnCodeFeedback && !isPrecursorAlreadyAdded && (
              <div className="space-y-2">
                {cbamRelevanceQuery.isFetching && (
                  <p className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="size-4 shrink-0 animate-spin" />
                    {t('configuration.precursorCn.cbamRelevanceChecking')}
                  </p>
                )}
                {cbamRelevanceQuery.isError && (
                  <p className="text-sm text-destructive">{t('configuration.loadError')}</p>
                )}
                {isPrecursorInGoodsList && (
                  <Alert variant="success">
                    <AlertDescription>
                      {t('configuration.precursorCn.inGoodsList')}
                    </AlertDescription>
                  </Alert>
                )}
                {isPrecursorNotInGoodsList && (
                  <Alert variant="destructive">
                    <AlertDescription className="space-y-3">
                      <p>{t('configuration.precursorCn.notInGoodsListNotice')}</p>
                      <p>{t('configuration.precursorCn.notInGoodsListAction')}</p>
                      <Button type="button" variant="outline" onClick={goToOtherMaterialCategories}>
                        {t('configuration.precursorCn.addAsOtherMaterial')}
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
            {isPrecursorInGoodsList && (
              <PrecursorSupplyOriginFields
                namePrefix="precursor-cn-create-supply"
                value={createSupplyOrigin}
                onChange={setCreateSupplyOrigin}
                t={t}
              />
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setCreateOpen(false)
                resetCreateForm()
              }}
            >
              {t('configuration.cancel')}
            </Button>
            <Button
              type="button"
              disabled={
                !hasValidPrecursorInput ||
                isPrecursorAlreadyAdded ||
                !isPrecursorInGoodsList ||
                cbamRelevanceQuery.isFetching ||
                mutations.addPrecursorCnCode.isPending
              }
              onClick={submitPrecursor}
            >
              {t('common:actions.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InstallationDeleteConfirmDialog
        pendingDelete={deleteConfirm.pendingDelete}
        open={deleteConfirm.isConfirmOpen}
        onOpenChange={(open) => {
          if (!open) {
            deleteConfirm.clearPendingDelete()
          }
        }}
        onConfirm={handleConfirmDelete}
        isLoading={mutations.removePrecursorCnCode.isPending}
      />

      <SourceGroupReferenceBlockedDialog
        open={sourceGroupBlocked !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSourceGroupBlocked(null)
          }
        }}
        title={t('configuration.sourceGroupReferenceBlocked.precursorCnTitle')}
        description={t('configuration.sourceGroupReferenceBlocked.precursorCnDescription', {
          label: sourceGroupBlocked?.label ?? '',
          count: sourceGroupBlocked?.count ?? 0,
        })}
        manageSourceGroupsHref={
          installationId && sourceGroupBlocked
            ? precursorCnCodeSourceGroupsPath({
                installationId,
                precursorCnCodeId: sourceGroupBlocked.id,
              })
            : undefined
        }
        manageSourceGroupsLabel={t(
          'configuration.sourceGroupReferenceBlocked.manageSourceGroups'
        )}
      />
    </div>
  )
}
